import { DeleteOutlined } from '@ant-design/icons';
import { App, Modal, Space } from 'antd';
import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { consumersActions } from '@store/consumers/consumers.slice';
import { credentialsActions } from '@store/credentials/credentials.slice';

import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Text } from '@atom/typography/typography';
import { RouteEnum } from '@enum/route.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useDeleteConsumerMutation } from '@hook/consumer/consumer.hooks';
import { useStoreDispatch } from '@hook/store/store.hooks';

interface DeleteConsumerProps {
  id: string;
}

export const DeleteConsumer = memo(({ id }: DeleteConsumerProps) => {
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const application = useCurrentApplication();
  const { action, state } = useMutation(useDeleteConsumerMutation(), credentialsActions);
  const { message } = App.useApp();

  const handleDelete = useCallback(
    () =>
      action({
        id,
        applicationId: application.current.id,
      }),
    [id, application.current.id, action],
  );
  const [open, setOpen] = useState(false);

  const showModal = () => setOpen(true);
  const onClose = () => setOpen(false);

  useEffect(() => {
    if (!state.isSuccess) return;

    void message.success('Consumer was successfully deleted');

    dispatch(consumersActions.removeConsumer(id));

    navigate(RouteEnum.Consumers.path);
  }, [state?.isSuccess, id, message]);

  return (
    <>
      <Modal
        open={open}
        title='Delete consumer'
        footer={
          <Space direction='horizontal'>
            <Button key='back' type={ButtonTypeEnum.Primary} ghost onClick={onClose}>
              Cancel
            </Button>
            <Button type={ButtonTypeEnum.Error} ghost onClick={handleDelete}>
              <DeleteOutlined />
              <Text color={ColorEnum.Red6} letterSpacing={1.4}>
                Delete
              </Text>
            </Button>
          </Space>
        }
        onCancel={onClose}
      >
        <Text level='m' paragraph>
          Are you sure to delete this consumer?
        </Text>
      </Modal>
      <Button type={ButtonTypeEnum.Error} onClick={showModal}>
        <DeleteOutlined />
        <Text color={ColorEnum.Red6} letterSpacing={1.4}>
          Delete consumer
        </Text>
      </Button>
    </>
  );
});
